import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, Loader2, MessageCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { waLink } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const HEADING = "font-['Manrope',sans-serif]";
const INPUT_DARK = "border-white/15 bg-white/5 text-white placeholder:text-slate-500 focus-visible:ring-[#00D1A8]/50";

function Lockup() {
  return (
    <div className="flex items-center gap-3">
      <img
        src="/nexora-logo.jpg"
        alt="Nexora"
        className="h-11 w-11 rounded-xl object-cover shadow-[0_0_18px_rgba(0,209,168,0.25)]"
      />
      <div className="leading-tight">
        <p className={`${HEADING} text-base font-extrabold tracking-tight text-white`}>Aplikasi Sekolah Musik</p>
        <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#00D1A8]">by Nexora</p>
      </div>
    </div>
  );
}

export default function Register() {
  const [form, setForm] = useState({ school_name: "", admin_name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [cfg, setCfg] = useState({ product_name: "Aplikasi Sekolah Musik", owner_wa: "" });

  useEffect(() => {
    api.get("/public/config").then((r) => setCfg(r.data)).catch(() => {});
  }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", form);
      setDone(data);
    } catch (err) {
      setError(apiError(err, "Pendaftaran gagal. Coba lagi."));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    const wa = cfg.owner_wa
      ? waLink(
          cfg.owner_wa,
          `Halo, saya ${done.name}, admin ${done.tenant?.name || "sekolah"}. Saya baru mendaftar Aplikasi Sekolah Musik by Nexora dan ingin mengaktifkan langganan.`
        )
      : null;
    return (
      <div className="relative flex min-h-screen items-center justify-center bg-[#040D1D] px-6 font-['Plus_Jakarta_Sans',sans-serif]" data-testid="register-success">
        <div className="pointer-events-none absolute -top-24 left-1/3 h-80 w-80 rounded-full bg-[#00D1A8]/10 blur-3xl" />
        <div className="relative w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.04] p-8 text-center shadow-2xl backdrop-blur-xl">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full border border-[#00D1A8]/30 bg-[#00D1A8]/10">
            <CheckCircle2 className="h-7 w-7 text-[#00D1A8]" />
          </div>
          <h1 className={`${HEADING} text-2xl font-extrabold text-white`}>Pendaftaran Berhasil!</h1>
          <p className="mt-3 text-sm leading-relaxed text-slate-400">
            Akun untuk <strong className="text-white">{done.tenant?.name}</strong> berhasil dibuat dengan status{" "}
            <strong className="text-[#00D1A8]">Menunggu Pembayaran</strong>. Hubungi tim kami untuk menyelesaikan
            pembayaran — akun Anda diaktifkan maksimal 1×24 jam setelah pembayaran terkonfirmasi.
          </p>
          {wa && (
            <a href={wa} target="_blank" rel="noreferrer" className="mt-6 block">
              <Button className="w-full gap-2 rounded-xl bg-gradient-to-r from-[#2563EB] to-[#00D1A8] font-semibold text-white shadow-[0_0_20px_rgba(0,209,168,0.3)] hover:opacity-95" data-testid="register-success-wa-button">
                <MessageCircle className="h-4 w-4" /> Konfirmasi Pembayaran via WhatsApp
              </Button>
            </a>
          )}
          <Link to="/login" className="mt-3 block">
            <Button variant="outline" className="w-full rounded-xl border-white/20 bg-white/10 text-white hover:bg-white/20" data-testid="register-success-login-button">
              Ke Halaman Masuk
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-[#040D1D] px-6 py-10 font-['Plus_Jakarta_Sans',sans-serif]" data-testid="register-page">
      <div className="pointer-events-none absolute -top-24 left-1/4 h-80 w-80 rounded-full bg-[#2563EB]/15 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 right-1/4 h-80 w-80 rounded-full bg-[#00D1A8]/10 blur-3xl" />
      <div className="relative w-full max-w-md">
        <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm font-medium text-slate-400 transition-colors hover:text-white" data-testid="back-to-landing-link">
          <ArrowLeft className="h-4 w-4" /> Kembali
        </Link>
        <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-8 shadow-2xl backdrop-blur-xl">
          <div className="mb-6">
            <Lockup />
          </div>
          <h1 className={`${HEADING} text-2xl font-extrabold tracking-tight text-white`}>Daftarkan Sekolah Anda</h1>
          <p className="mt-1 text-sm text-slate-400">Isi data berikut — akun aktif setelah pembayaran dikonfirmasi oleh tim kami.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            {error && (
              <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-400" data-testid="register-error">
                {error}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="school_name" className="text-slate-300">Nama Sekolah Musik</Label>
              <Input id="school_name" data-testid="register-school-name" placeholder="cth: Gihon Cantata" value={form.school_name} onChange={set("school_name")} className={INPUT_DARK} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="admin_name" className="text-slate-300">Nama Admin / Pemilik</Label>
              <Input id="admin_name" data-testid="register-admin-name" placeholder="Nama lengkap" value={form.admin_name} onChange={set("admin_name")} className={INPUT_DARK} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input id="email" type="email" data-testid="register-email" placeholder="admin@sekolah.id" value={form.email} onChange={set("email")} className={INPUT_DARK} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-slate-300">Nomor WhatsApp</Label>
              <Input id="phone" data-testid="register-phone" placeholder="08xxxxxxxxxx" value={form.phone} onChange={set("phone")} className={INPUT_DARK} required />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-300">Kata Sandi</Label>
              <Input id="password" type="password" data-testid="register-password" placeholder="Minimal 8 karakter" value={form.password} onChange={set("password")} className={INPUT_DARK} required minLength={8} />
            </div>
            <Button type="submit" className="w-full rounded-xl bg-gradient-to-r from-[#2563EB] to-[#00D1A8] font-semibold text-white shadow-[0_0_20px_rgba(0,209,168,0.3)] transition-opacity duration-200 hover:opacity-95" disabled={loading} data-testid="register-submit-button">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Daftar Sekarang
            </Button>
          </form>
          <p className="mt-5 text-center text-sm text-slate-400">
            Sudah punya akun?{" "}
            <Link to="/login" className="font-semibold text-[#00D1A8] transition-colors hover:text-teal-300" data-testid="register-login-link">
              Masuk
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
